import { columnsTooltipWidthCh, renderColumnsInfoTooltip } from "./tooltip";
import type { AuthGroup } from "../types/api";
import { el } from "../utils/dom";
import { ENTITY_ORDER, entityLabel } from "../utils/format";

export type AccessGroupPickerOptions = {
  groups: AuthGroup[];
  selectedIds: Set<number>;
  query: string;
  disabled?: boolean;
  /** When true, list scrolls inside a capped panel (edit flow). Creation uses page scroll. */
  constrainListHeight?: boolean;
  onQueryChange: (query: string) => void;
  onToggle: (groupId: number) => void;
  onToggleVisible: (select: boolean) => void;
};

export function filterAccessGroups(groups: AuthGroup[], query: string): AuthGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;
  return groups.filter((group) => {
    const haystack = `${group.name} ${entityLabel(group.entity)} ${group.entity}`.toLowerCase();
    return haystack.includes(q);
  });
}

export function renderAccessGroupPicker(options: AccessGroupPickerOptions): HTMLElement {
  const {
    groups,
    selectedIds,
    disabled,
    constrainListHeight = false,
    onQueryChange,
    onToggle,
    onToggleVisible,
  } = options;
  let query = options.query;
  const tooltipWidthCh = columnsTooltipWidthCh(groups);

  if (!groups.length) {
    return el("p", { className: "next-cron__empty" }, [
      "No access groups exist yet. Create one under Access Groups before assigning data access.",
    ]);
  }

  const listHost = el("div", {
    className: "access-group-picker__list",
    dataset: { toggleList: "groups" },
  });

  const paintList = (): void => {
    const visible = filterAccessGroups(groups, query);
    const allVisibleSelected =
      visible.length > 0 && visible.every((group) => selectedIds.has(group.id));

    const selectLabel = allVisibleSelected
      ? visible.length === groups.length
        ? "Deselect all"
        : "Deselect matches"
      : visible.length === groups.length
        ? "Select all"
        : "Select matches";

    listHost.replaceChildren(
      el("div", { className: "access-group-picker__select-all" }, [
        el("button", {
          className: "toggle",
          type: "button",
          role: "switch",
          "aria-label": "Select all visible access groups",
          "aria-checked": String(allVisibleSelected),
          dataset: { selectAll: "" },
          disabled: Boolean(disabled) || visible.length === 0,
          onclick: () => {
            // Read live selection — do not close over paint-time allVisibleSelected
            // or select/deselect gets stuck until the list is rebuilt.
            const currentVisible = filterAccessGroups(groups, query);
            const currentlyAll =
              currentVisible.length > 0 &&
              currentVisible.every((group) => selectedIds.has(group.id));
            onToggleVisible(!currentlyAll);
          },
        }),
        el("span", { dataset: { selectAllLabel: "" } }, [selectLabel]),
      ]),
      ...renderGroupedSections(visible, selectedIds, disabled, onToggle, tooltipWidthCh)
    );
  };

  const searchInput = el("input", {
    className: "input input--sm",
    type: "text",
    placeholder: "Search access groups…",
    "aria-label": "Search access groups",
    value: query,
    disabled: Boolean(disabled),
    oninput: (ev: Event) => {
      query = (ev.target as HTMLInputElement).value;
      onQueryChange(query);
      paintList();
    },
  }) as HTMLInputElement;

  paintList();

  const rootClass = constrainListHeight
    ? "access-group-picker access-group-picker--constrained"
    : "access-group-picker";

  return el("div", { className: rootClass, dataset: { groupsPicker: "" } }, [
    el("div", { className: "field--search field--search-grow" }, [searchInput]),
    listHost,
  ]);
}

/** Patch toggle/select-all aria state without rebuilding the list. */
export function syncAccessGroupPickerUi(
  root: ParentNode,
  groups: AuthGroup[],
  selectedIds: Set<number>,
  query: string
): void {
  const list = root.querySelector("[data-toggle-list='groups']");
  if (!list) return;

  const visible = filterAccessGroups(groups, query);
  const allVisibleSelected =
    visible.length > 0 && visible.every((group) => selectedIds.has(group.id));
  list.querySelector("[data-select-all]")?.setAttribute("aria-checked", String(allVisibleSelected));

  const label = list.querySelector("[data-select-all-label]");
  if (label) {
    label.textContent = allVisibleSelected
      ? visible.length === groups.length
        ? "Deselect all"
        : "Deselect matches"
      : visible.length === groups.length
        ? "Select all"
        : "Select matches";
  }

  for (const toggle of list.querySelectorAll<HTMLElement>("[data-item-id]")) {
    const id = Number(toggle.dataset.itemId);
    toggle.setAttribute("aria-checked", String(selectedIds.has(id)));
  }
}

function renderGroupedSections(
  groups: AuthGroup[],
  selectedIds: Set<number>,
  disabled: boolean | undefined,
  onToggle: (groupId: number) => void,
  tooltipWidthCh: number
): HTMLElement[] {
  if (!groups.length) {
    return [
      el("p", { className: "access-group-picker__empty" }, ["No access groups match this search."]),
    ];
  }

  return groupByEntity(groups).map(({ entity, groups: sectionGroups }) =>
    el("section", { className: "access-group-picker__section" }, [
      el("h3", { className: "access-group-picker__section-title" }, [entityLabel(entity)]),
      el(
        "div",
        { className: "access-group-picker__section-rows" },
        sectionGroups.map((group) =>
          groupRow(group, selectedIds.has(group.id), disabled, onToggle, tooltipWidthCh)
        )
      ),
    ])
  );
}

function groupRow(
  group: AuthGroup,
  selected: boolean,
  disabled: boolean | undefined,
  onToggle: (groupId: number) => void,
  tooltipWidthCh: number
): HTMLElement {
  return el("div", { className: "access-group-picker__row" }, [
    el("button", {
      className: "toggle",
      type: "button",
      role: "switch",
      "aria-label": `Assign ${group.name}`,
      "aria-checked": String(selected),
      dataset: { itemId: String(group.id) },
      disabled: Boolean(disabled),
      onclick: () => onToggle(group.id),
    }),
    el("span", { className: "access-group-picker__name label-with-help" }, [
      group.name,
      renderColumnsInfoTooltip(group.columns, { widthCh: tooltipWidthCh }),
    ]),
  ]);
}

function groupByEntity(groups: AuthGroup[]): { entity: string; groups: AuthGroup[] }[] {
  const byEntity = new Map<string, AuthGroup[]>();
  for (const group of groups) {
    const bucket = byEntity.get(group.entity) ?? [];
    bucket.push(group);
    byEntity.set(group.entity, bucket);
  }

  const ordered: { entity: string; groups: AuthGroup[] }[] = [];
  const seen = new Set<string>();
  for (const entity of ENTITY_ORDER) {
    const section = byEntity.get(entity);
    if (!section?.length) continue;
    ordered.push({ entity, groups: section });
    seen.add(entity);
  }
  for (const [entity, section] of byEntity) {
    if (seen.has(entity)) continue;
    ordered.push({ entity, groups: section });
  }
  return ordered;
}
